/**
 * V2Sidebar — collapsible navigation rail for the V2 shell.
 *
 * Mirrors design_reference/js/shell.jsx Sidebar: brand block, Workspace /
 * Manage / Reference nav groups, collapse toggle + user block footer.
 * Role-gates Manage items via useAuthStore().roles. Active item uses
 * longest-path match (mirrors classic Sidebar.tsx).
 *
 * The footer also carries the app's only PERMANENT feedback entry point
 * (B-969). Until now FeedbackDialog had exactly one call site — inside
 * GetCreditsModal — so the only way to tell us anything was to first run out
 * of credits. Someone who tries one conversion, isn't impressed and leaves is
 * precisely the person we most need to hear from, and they never hit that
 * modal.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import useAuthStore from '../../stores/authStore';
import { cn } from '../../lib/utils';
import { useUiStore } from '../theme/uiStore';
import { BRANDS } from '../branding/registry';
import { Avatar } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Modal } from '../components/ui/modal';
import { Pill } from '../components/ui/pill';
import FeedbackDialog from '../components/FeedbackDialog';
import {
  IconBook,
  IconChat,
  IconFeedback,
  IconClipboard,
  IconDashboard,
  IconHammer,
  IconLayers,
  IconList,
  IconLogout,
  IconPanel,
  IconPlug,
  IconSettings,
  IconShield,
} from '../components/icons';

/** A single navigation entry. */
interface NavItem {
  label: string;
  href: string;
  icon: ReactNode;
  /** When set, the item only shows if the user holds one of these roles. */
  requiresAnyRole?: string[];
  /** When true, role-less (pay-per-conversion) users see the item with a
   *  LOCKED pill instead of it being hidden — the route shows the lock card. */
  lockedForNonMembers?: boolean;
}

/** A labelled group of nav items. */
interface NavGroup {
  section: string;
  items: NavItem[];
}

const ADMIN_ROLES = ['admin', 'super_admin'];
const RULE_ROLES = ['editor', 'expert', 'reviewer', 'admin', 'super_admin'];

const NAV_GROUPS: NavGroup[] = [
  {
    section: 'Workspace',
    items: [
      { label: 'Dashboard', href: '/', icon: <IconDashboard /> },
      {
        label: 'Scope Builder',
        href: '/scope/builder',
        icon: <IconHammer />,
        lockedForNonMembers: true,
      },
      { label: 'Supplement', href: '/completeness', icon: <IconClipboard /> },
      // PRP-MCP-10 G5: open to everyone, no badge. A free account sees the subscribe line on the page.
      { label: 'Comparisons', href: '/comparisons', icon: <IconLayers /> },
      { label: 'AI Chat', href: '/chat', icon: <IconChat />, lockedForNonMembers: true },
      { label: 'AI Assistant', href: '/assistant', icon: <IconPlug /> },
      // PRP-MCP-12 G7: the user's own macros, open to free users.
      { label: 'Macros', href: '/macros', icon: <IconList /> },
    ],
  },
  {
    section: 'Manage',
    items: [
      {
        label: 'Rule Library',
        href: '/admin/rules',
        icon: <IconBook />,
        requiresAnyRole: RULE_ROLES,
      },
      {
        label: 'Rule Suggestions',
        href: '/admin/rule-suggestions',
        icon: <IconClipboard />,
        requiresAnyRole: ['editor', 'expert', 'admin', 'super_admin'],
      },
      {
        label: 'Admin',
        href: '/admin',
        icon: <IconShield />,
        requiresAnyRole: ADMIN_ROLES,
      },
      // PRP-MCP-10 G1: the affiliate program (admin pages render in the classic shell).
      { label: 'Affiliates', href: '/admin/affiliates', icon: <IconShield />, requiresAnyRole: ADMIN_ROLES },
      // PRP-MCP-12 G3: suggestions users sent from chat.
      { label: 'Suggestions', href: '/admin/suggestions', icon: <IconClipboard />, requiresAnyRole: ADMIN_ROLES },
      { label: 'Settings', href: '/profile', icon: <IconSettings /> },
    ],
  },
  {
    section: 'Reference',
    items: [
      {
        label: 'Design System',
        href: '/v2/design',
        icon: <IconLayers />,
        requiresAnyRole: ['super_admin'],
      },
    ],
  },
];

/** Props for {@link V2Sidebar}. */
export interface V2SidebarProps {
  /** When true, the rail collapses to icons only. */
  collapsed: boolean;
  /** Toggles the collapsed state (owned by V2Layout). */
  setCollapsed: (_value: boolean | ((_prev: boolean) => boolean)) => void;
  /** Phone width: whether the drawer is open (B-7305). Ignored at md and up. */
  mobileOpen?: boolean;
  /** Phone width: closes the drawer (called when a destination is picked). */
  onCloseMobile?: () => void;
}

/**
 * Renders the V2 sidebar rail.
 *
 * @param props - Collapsed state + setter (lifted to V2Layout for the grid).
 * @returns The sidebar element.
 */
export function V2Sidebar({
  collapsed,
  setCollapsed,
  mobileOpen = false,
  onCloseMobile,
}: V2SidebarProps) {
  const location = useLocation();
  const roles = useAuthStore((s) => s.roles);
  const rolesLoaded = useAuthStore((s) => s.rolesLoaded);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const brand = useUiStore((s) => s.brand);
  const brandDef = BRANDS[brand] ?? BRANDS.standard;
  const BrandLogo = brandDef.Logo;
  // Sign-out is destructive-feeling for users who tap it by accident — confirm first.
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Hide items the user isn't allowed to see.
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        !item.requiresAnyRole ||
        item.requiresAnyRole.some((r) => roles.includes(r)),
    ),
  })).filter((group) => group.items.length > 0);

  // Longest-match-wins active route (mirrors classic Sidebar.tsx:78-85).
  const allVisible = visibleGroups.flatMap((g) => g.items);
  const activeHref = allVisible.reduce<string | null>((best, item) => {
    const matches =
      location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href + '/'));
    if (!matches) return best;
    if (!best || item.href.length > best.length) return item.href;
    return best;
  }, null);

  const userLabel = user?.displayName || user?.email || 'Account';

  return (
    <aside
      id="v2-sidebar"
      data-open={mobileOpen ? 'true' : 'false'}
      className={cn(
        'v2-sidebar flex min-h-0 flex-col border-r border-[var(--bd-subtle)] bg-[var(--bg-1)]',
        // Phone: an off-canvas drawer, hidden and unfocusable until opened.
        'max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-40 max-md:w-[272px] max-md:max-w-[85vw] max-md:transition-transform',
        mobileOpen ? 'max-md:translate-x-0' : 'max-md:invisible max-md:-translate-x-full',
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          'v2-sb-brand flex h-[var(--topbar-h)] items-center gap-[10px] border-b border-[var(--bd-subtle)] px-[14px]',
          collapsed && 'justify-center px-0',
        )}
      >
        <span className="v2-sb-logo grid h-7 w-7 flex-none place-items-center rounded-[var(--r-md)] bg-[radial-gradient(120%_120%_at_30%_20%,color-mix(in_oklab,var(--accent)_30%,var(--bg-3)),var(--bg-3))]">
          <BrandLogo size={17} />
        </span>
        {!collapsed && (
          <span className="v2-sb-word leading-tight">
            <span className="block text-[13px] font-[560] text-[var(--tx-hi)]">
              {brandDef.wordmark}
            </span>
            <small className="block text-[11px] text-[var(--tx-lo)]">
              {brandDef.sub}
            </small>
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto p-[10px]">
        {visibleGroups.map((group) => (
          <div key={group.section}>
            {!collapsed && (
              <div className="v2-sb-sec whitespace-nowrap px-[10px] pb-[6px] pt-[14px] text-[10px] font-semibold uppercase tracking-[.09em] text-[var(--tx-faint)]">
                {group.section}
              </div>
            )}
            {group.items.map((item) => {
              const active = activeHref === item.href;
              const gated = !!item.requiresAnyRole;
              const locked =
                !!item.lockedForNonMembers && rolesLoaded && roles.length === 0;
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  title={item.label}
                  onClick={onCloseMobile}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'v2-nav-item',
                    active && 'is-active',
                    'relative flex h-9 items-center gap-[10px] rounded-[var(--r-md)] border border-transparent px-[10px] text-[12.5px] font-[520]',
                    'text-[var(--tx-mid)] [&_svg]:h-[17px] [&_svg]:w-[17px] [&_svg]:flex-none [&_svg]:text-[var(--tx-lo)]',
                    'hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)] hover:[&_svg]:text-[var(--tx-mid)]',
                    active &&
                      'bg-[var(--bg-3)] text-[var(--tx-hi)] border-[var(--bd-subtle)] shadow-[inset_0_1px_0_var(--hi-top)] [&_svg]:text-[var(--accent-ink)]',
                    collapsed && 'justify-center px-0',
                  )}
                >
                  {active && (
                    <span className="v2-nav-marker absolute left-[-10px] bottom-[7px] top-[7px] w-[2.5px] rounded-full bg-[var(--accent-ink)]" />
                  )}
                  {item.icon}
                  {!collapsed && <span className="flex-1">{item.label}</span>}
                  {!collapsed && gated && (
                    <Pill
                      kind="neutral"
                      className="v2-nav-gate h-4 px-[6px] text-[9.5px]"
                    >
                      ADMIN
                    </Pill>
                  )}
                  {!collapsed && locked && (
                    <Pill
                      kind="neutral"
                      className="v2-nav-gate h-4 px-[6px] text-[9.5px]"
                    >
                      LOCKED
                    </Pill>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="v2-sb-foot border-t border-[var(--bd-subtle)] p-2">
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          title="Send feedback"
          className={cn(
            'v2-sb-feedback flex w-full items-center gap-[10px] rounded-[var(--r-sm)] px-2 py-2 text-[12px] text-[var(--tx-lo)]',
            'hover:bg-[var(--bg-3)] hover:text-[var(--tx-mid)] [&_svg]:h-4 [&_svg]:w-4',
            collapsed && 'justify-center',
          )}
        >
          <IconFeedback />
          {!collapsed && <span>Send feedback</span>}
        </button>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className={cn(
            'v2-sb-collapse max-md:hidden flex w-full items-center gap-[10px] rounded-[var(--r-sm)] px-2 py-2 text-[12px] text-[var(--tx-lo)]',
            'hover:bg-[var(--bg-3)] hover:text-[var(--tx-mid)] [&_svg]:h-4 [&_svg]:w-4',
            collapsed && 'justify-center',
          )}
        >
          <IconPanel />
          {!collapsed && <span>Collapse</span>}
        </button>
        <div
          className={cn(
            'v2-sb-user flex items-center gap-[10px] rounded-[var(--r-sm)] p-2 hover:bg-[var(--bg-3)]',
            collapsed && 'justify-center',
          )}
        >
          <Avatar name={userLabel} size={28} />
          {!collapsed && (
            <span className="v2-sb-user-meta min-w-0 leading-[1.2]">
              <b className="block truncate text-[12.5px] font-[550] text-[var(--tx-hi)]">
                {user?.displayName || 'Account'}
              </b>
              <span className="block max-w-[130px] truncate text-[11px] text-[var(--tx-lo)]">
                {user?.email || ''}
              </span>
            </span>
          )}
          {!collapsed && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto w-7 justify-center px-0"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => setConfirmSignOut(true)}
            >
              <IconLogout />
            </Button>
          )}
        </div>
      </div>
      {feedbackOpen && <FeedbackDialog onClose={() => setFeedbackOpen(false)} />}
      {confirmSignOut && (
        <Modal
          label="Confirm sign out"
          title="Sign out?"
          subtitle="You'll need to sign in again to access your projects."
          onClose={() => setConfirmSignOut(false)}
          testId="signout-confirm"
          className="max-w-sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmSignOut(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => logout()}>
                Sign out
              </Button>
            </>
          }
        >
          <span className="text-[12.5px] text-[var(--tx-mid)]">
            Any unsaved edits on this screen will be lost.
          </span>
        </Modal>
      )}
    </aside>
  );
}
